import { createSelector } from '@reduxjs/toolkit';
import { selectIsJobTitleHiddenForMyCommunity } from 'community/api';
import prop from 'lodash/fp/prop';
import { selectUserAccountId } from 'user/api/selectors';

import { eventApi } from './eventApi';
import { getEventDetailsCacheIds } from './utils';

import type { RootState } from 'store';

const selectEventDetailsById = eventApi.endpoints.getEventDetails.select;

const selectActiveEventId = (state: RootState) => state.event.activeEventId!;
// The id the active event was fetched by (numeric or UUID publicId) — must match the getEventDetails
// cache key, which is keyed by the fetch arg, not by the numeric activeEventId (HNS-148).
const selectActiveEventFetchId = (state: RootState) => state.event.activeEventFetchId ?? state.event.activeEventId!;

// Memoize the args object to ensure stable reference for RTK Query selectors
const selectEventDetailsArgs = createSelector([selectActiveEventFetchId], (id) => ({ id }));

const selectEventQuestionsArgs = createSelector([selectActiveEventId], (activeEventId) => ({ id: activeEventId }));

// TODO Replace with `selectEventDetailsById` only when event hash migration finished
export const selectActiveEventDetails = createSelector(
  [selectEventDetailsArgs, (state: RootState) => state],
  (args, state) => {
    const result = selectEventDetailsById(args)(state);
    return result?.data;
  }
);

export const selectActiveEventQuestions = createSelector(
  [selectEventQuestionsArgs, (state: RootState) => state],
  (args, state) => {
    const result = eventApi.endpoints.getEventQuestions.select(args)(state);
    return result?.data;
  }
);

export const selectIsUserRegistered = createSelector(selectActiveEventDetails, prop('isUserRegistered'));

export const selectHasCompletedPreEventQuestions = createSelector(
  selectActiveEventDetails,
  prop('hasCompletedPreEventQuestions')
);

export const selectHasCompletedPostEventQuestions = createSelector(
  selectActiveEventDetails,
  prop('hasCompletedPostEventQuestions')
);

export const selectEventStatus = createSelector(selectActiveEventDetails, prop('status'));

export const selectEventName = createSelector(selectActiveEventDetails, prop('name'));

export const selectEventPin = createSelector(selectActiveEventDetails, prop('pin'));
export const selectEventPinEnabled = createSelector(selectActiveEventDetails, prop('isPinEnabled'));
export const selectEventCommunityId = createSelector(selectActiveEventDetails, prop('communityId'));
// The event community's roleFieldDisabled (HNS-5): when true, the interactive entry flow skips the
// job-title MemberSetup dialog and joins with an empty role. See useEnterEvent.
export const selectEventRoleFieldDisabled = createSelector(selectActiveEventDetails, prop('roleFieldDisabled'));
/**
 * Whether the job title must be hidden everywhere the *active* event displays it (HNS-404).
 *
 * The event's community flag wins whenever event details are loaded: inside the live room the
 * user's active community can differ from the event's, because `useSwitchToEventCommunity` only
 * runs on the pre-join details page. Falls back to the active community's setting when no event
 * details are loaded (e.g. the new-event wizard host editor).
 *
 * Display-only — stored job titles are never cleared, so turning the setting back on restores them.
 */
export const selectIsJobTitleHiddenInEvent = createSelector(
  selectEventRoleFieldDisabled,
  selectIsJobTitleHiddenForMyCommunity,
  (eventRoleFieldDisabled, isHiddenForMyCommunity) => eventRoleFieldDisabled ?? isHiddenForMyCommunity
);

/**
 * The same decision as `selectIsJobTitleHiddenInEvent` but for an event named by id rather than the
 * active one — for callers (e.g. the attendees CSV export) that know the exact event.
 *
 * The `getEventDetails` cache is keyed by the exact arg the page fetched with, which is type-sensitive
 * and may be a UUID publicId or a stringified id rather than the numeric `eventId` (HNS-148/HNS-238),
 * so every candidate key has to be tried — `getEventDetailsCacheIds` enumerates them.
 */
export const selectIsJobTitleHiddenForEvent = (state: RootState, eventId: number): boolean => {
  const cachedDetails = getEventDetailsCacheIds(state, eventId)
    .map((cacheId) => selectEventDetailsById({ id: cacheId })(state).data)
    .find((data) => data !== undefined);

  return cachedDetails?.roleFieldDisabled ?? selectIsJobTitleHiddenForMyCommunity(state);
};
/** Use when want to omit host's privileges */
export const selectIsEventOwner = createSelector(
  selectActiveEventDetails,
  selectUserAccountId,
  (event, userId) => event?.owner.id === userId
);
